import { getCourtLabel, getFeaturedCourtLabel } from '@pickleballinc/lib/court-title';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import moment from 'moment';

import { MatchPropsShort } from '@/modules/results/types';
import type { MatchExtended } from '@/modules/ticker/types';

const getFormattedRoundText = (roundText?: string) => {
  if (!roundText) {
    return '';
  }

  if (roundText.startsWith('Round')) {
    const roundTextSplit = roundText.split(' ');
    let formattedRoundText = '';

    if (roundTextSplit[1] && roundTextSplit[1] === 'of') {
      return roundText;
    }

    if (roundTextSplit[0]) {
      formattedRoundText += roundTextSplit[0];
    }

    if (roundTextSplit[1]) {
      formattedRoundText += ' of ';
      formattedRoundText += roundTextSplit[1];
    }

    return formattedRoundText;
  }

  return roundText;
};

type MatchCardType = 'TICKER' | 'MATCH' | 'RESULT' | 'MATCHV3';

export interface FormatMatchInfoSnakeCaseProps {
  matchInfos: MatchPropsShort[];
  imagesWidth: number;
  imagesHeight: number;
  matchCardType?: MatchCardType;
}

export function formatMatchInfoSnakeCase({
  matchInfos,
  imagesWidth,
  imagesHeight,
  matchCardType,
}: FormatMatchInfoSnakeCaseProps) {
  if (matchInfos.length === 0) return [];

  let filteredMatchInfos = [...matchInfos];
  if (matchCardType === 'TICKER') {
    filteredMatchInfos = matchInfos.filter((match) => {
      const tournamentLocalDate = moment().utc().add(Number(match.utc_offset), 'seconds');
      return (
        (match.local_date_match_completed &&
          tournamentLocalDate.isSame(moment(match.local_date_match_completed).utc(), 'day')) ||
        (match.local_date_match_assigned_to_court &&
          tournamentLocalDate.isSame(
            moment(match.local_date_match_assigned_to_court).utc(),
            'day'
          )) ||
        (match.local_date_match_start &&
          tournamentLocalDate.isSame(moment(match.local_date_match_start).utc(), 'day')) ||
        (match.local_date_match_planned_start &&
          tournamentLocalDate.isSame(moment(match.local_date_match_planned_start).utc(), 'day'))
      );
    });
  }

  const matches: MatchExtended[] = filteredMatchInfos.map((matchInfo) => {
    const teamOneScores = [matchInfo.team_one_game_one_score];
    const teamTwoScores = [matchInfo.team_two_game_one_score];
    const gamesStatus: number[] = [+matchInfo.game_one_status];

    const teamOnePlayers = [
      {
        playerId: matchInfo.team_one_player_one_uuid,
        firstName: matchInfo.team_one_player_one_first_name,
        lastName: matchInfo.team_one_player_one_last_name,
        suffixName: matchInfo.team_one_player_one_name_suffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.team_one_player_one_picture
            : getCDNURL(matchInfo.team_one_player_one_picture, imagesWidth, imagesHeight),
      },
    ];
    const teamTwoPlayers = [
      {
        playerId: matchInfo.team_two_player_one_uuid,
        firstName: matchInfo.team_two_player_one_first_name,
        lastName: matchInfo.team_two_player_one_last_name,
        suffixName: matchInfo.team_two_player_one_name_suffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.team_two_player_one_picture
            : getCDNURL(matchInfo.team_two_player_one_picture, imagesWidth, imagesHeight),
      },
    ];

    if (matchInfo.team_one_player_two_uuid) {
      teamOnePlayers.push({
        playerId: matchInfo.team_one_player_two_uuid,
        firstName: matchInfo.team_one_player_two_first_name,
        lastName: matchInfo.team_one_player_two_last_name,
        suffixName: matchInfo.team_one_player_two_name_suffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.team_one_player_two_picture
            : getCDNURL(matchInfo.team_one_player_two_picture, imagesWidth, imagesHeight),
      });
    }

    if (matchInfo.team_two_player_two_uuid) {
      teamTwoPlayers.push({
        playerId: matchInfo.team_two_player_two_uuid,
        firstName: matchInfo.team_two_player_two_first_name,
        lastName: matchInfo.team_two_player_two_last_name,
        suffixName: matchInfo.team_two_player_two_name_suffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.team_two_player_two_picture
            : getCDNURL(matchInfo.team_two_player_two_picture, imagesWidth, imagesHeight),
      });
    }

    if (matchInfo.score_format_game_best_out_of > 1) {
      teamOneScores.push(matchInfo.team_one_game_two_score);
      teamTwoScores.push(matchInfo.team_two_game_two_score);
      gamesStatus.push(+matchInfo.game_two_status);
    }

    if (matchInfo.score_format_game_best_out_of > 2) {
      teamOneScores.push(matchInfo.team_one_game_three_score);
      teamTwoScores.push(matchInfo.team_two_game_three_score);
      gamesStatus.push(+matchInfo.game_three_status);
    }

    if (matchInfo.score_format_game_best_out_of > 3) {
      teamOneScores.push(matchInfo.team_one_game_four_score);
      teamTwoScores.push(matchInfo.team_two_game_four_score);
      gamesStatus.push(+matchInfo.game_four_status);
    }

    if (matchInfo.score_format_game_best_out_of > 4) {
      teamOneScores.push(matchInfo.team_one_game_five_score);
      teamTwoScores.push(matchInfo.team_two_game_five_score);
      gamesStatus.push(+matchInfo.game_five_status);
    }

    const formattedRoundText = getFormattedRoundText(matchInfo.round_text);

    let matchObject: MatchExtended = {
      id: matchInfo.match_uuid,
      timezoneAbbreviation: matchInfo.timezone_abbreviation,
      matchStatus: matchInfo.match_status,
      matchCompletedType: matchInfo.match_completed_type,
      currentServingNumber: matchInfo.current_serving_number,
      server: matchInfo.server,
      serverFromTeam: matchInfo.server_from_team,
      team1: {
        players: [...teamOnePlayers],
        scores: [...teamOneScores],
        isWinner: matchInfo.match_status === 4 && matchInfo.winner === 1,
      },
      team2: {
        players: [...teamTwoPlayers],
        scores: [...teamTwoScores],
        isWinner: matchInfo.match_status === 4 && matchInfo.winner === 2,
      },
      gamesStatus: gamesStatus,
      roundNumber:
        formattedRoundText || (matchInfo.round_number ? `Round ${matchInfo.round_number}` : ''),
    };

    if (matchCardType === 'TICKER') {
      matchObject = {
        ...matchObject,
        courtName: getFeaturedCourtLabel(matchInfo.court_title),
        tournamentTitle: matchInfo.tournament_title,
        matchTimeStart: formatDateTime(
          matchInfo?.local_date_match_completed ||
            matchInfo?.local_date_match_start ||
            matchInfo?.local_date_match_planned_start,
          { type: 'time' }
        ),
      };
    } else if (matchCardType === 'MATCHV3') {
      matchObject = {
        ...matchObject,
        tournamentTitle: matchInfo.tournament_title,
        eventTitle: matchInfo.event_title,
        courtName: matchInfo.court_title ? `Court ${getCourtLabel(matchInfo.court_title)}` : '',
        matchTime: formatDateTime(
          matchInfo?.local_date_match_completed ||
            matchInfo?.local_date_match_start ||
            matchInfo?.local_date_match_planned_start
        ),
        matchCardImageUrl: matchInfo.match_card_image_url,
      };
    } else {
      matchObject = {
        ...matchObject,
        eventTitle: matchInfo.event_title,
        courtName: matchInfo.court_title ? `Court ${getCourtLabel(matchInfo.court_title)}` : '',
        matchTime: formatDateTime(
          matchInfo?.local_date_match_completed ||
            matchInfo?.local_date_match_start ||
            matchInfo?.local_date_match_planned_start,
          { type: 'time' }
        ),
        matchTimeStart: formatDateTime(
          matchInfo?.local_date_match_completed ||
            matchInfo?.local_date_match_start ||
            matchInfo?.local_date_match_planned_start,
          { type: 'datetime', includeYear: false }
        ),
      };
    }

    if (matchCardType === 'RESULT') {
      matchObject = {
        ...matchObject,
        gameOneEndDate: matchInfo.game_one_end_date
          ? new Date(matchInfo.game_one_end_date)
          : undefined,
        gameTwoEndDate: matchInfo.game_two_end_date
          ? new Date(matchInfo.game_two_end_date)
          : undefined,
        gameThreeEndDate: matchInfo.game_three_end_date
          ? new Date(matchInfo.game_three_end_date)
          : undefined,
        gameFourEndDate: matchInfo.game_four_end_date
          ? new Date(matchInfo.game_four_end_date)
          : undefined,
        gameFiveEndDate: matchInfo.game_five_end_date
          ? new Date(matchInfo.game_five_end_date)
          : undefined,
      };
    }

    return matchObject;
  });

  return matches;
}
